import { ref, computed } from 'vue'
import { viewPresetsRepository } from '@/infrastructure/repositories/ViewPresetsRepository'
import type { ViewPresetDocument } from '@/interfaces/firestore/fields-management'

// Estado singleton a nivel de módulo (mismo patrón que useSystemFieldsConfig):
// la vista asignada es una sola por usuario+compañía y la consultan varios
// consumidores (tabla, stats, fetch de embarques).
const assignedView = ref<ViewPresetDocument | null>(null)
const loadedKey = ref<string | null>(null)
const isLoading = ref(false)
let pendingLoad: Promise<ViewPresetDocument | null> | null = null
let pendingKey: string | null = null

/**
 * Vista de embarques asignada por un admin al usuario actual.
 * Si existe, el usuario entra en "modo restringido": los datos vienen
 * exclusivamente de BigQuery con los filtros de la vista aplicados server-side.
 */
export function useAssignedShipmentView() {
  const hasAssignedView = computed(() => assignedView.value !== null)

  /** Filtros obligatorios de la vista (formato activeFilters). */
  const baseFilters = computed<Record<string, any>>(() => assignedView.value?.filters || {})

  /**
   * Carga la vista asignada para el usuario+compañía. Cachea por clave, así que
   * es seguro llamarla desde varios consumidores; `force` refresca.
   * Los admins nunca quedan restringidos (isAdmin ⇒ sin vista asignada).
   */
  const loadAssignedView = async (
    companyId: string,
    userId: string,
    options: { isAdmin?: boolean; force?: boolean } = {}
  ): Promise<ViewPresetDocument | null> => {
    if (!companyId || !userId) return null

    const key = `${companyId}_${userId}`
    if (!options.force && loadedKey.value === key) return assignedView.value
    if (pendingLoad && pendingKey === key && !options.force) return pendingLoad

    isLoading.value = true
    pendingKey = key
    pendingLoad = (async () => {
      try {
        if (options.isAdmin) {
          assignedView.value = null
        } else {
          const presets = await viewPresetsRepository.getAssignedPresets('shipments', userId, companyId)
          // v1: una sola vista asignada por usuario; solo cuentan las que tienen filtros
          assignedView.value = presets.find(p => p.filters !== undefined) ?? null
        }
        loadedKey.value = key
        return assignedView.value
      } catch (error) {
        console.error('Error loading assigned shipment view:', error)
        assignedView.value = null
        return null
      } finally {
        isLoading.value = false
        pendingLoad = null
        pendingKey = null
      }
    })()

    return pendingLoad
  }

  return {
    assignedView,
    hasAssignedView,
    baseFilters,
    isLoadingAssignedView: isLoading,
    loadAssignedView,
  }
}
